import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Component,
  inject,
  Injector,
  OnInit,
  signal,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AnnouncementBanners } from '@app/features/announcements/announcement-banners/announcement-banners';
import { AnomalyBanners } from '@app/features/anomalies/anomaly-banners/anomaly-banners';
import { ContractEventHistory } from '@app/features/contracts/contract-event-history/contract-event-history';
import { ContractListItem } from '@app/features/contracts/contract.model';
import { ValidationRequestService } from '@app/features/contracts/validation/validation-request-service';
import { DocumentService } from '@app/features/documents/document-service';
import { DocumentViewer } from '@app/features/documents/document-viewer/document-viewer';
import { CdTDocument } from '@app/features/documents/document.model';
import { DemoBanner } from '@app/features/tours/demo/demo-banner';
import { DEMO_TOUR_ID } from '@app/features/tours/demo/demo-mode';
import { TourPanel } from '@app/features/tours/tour-panel/tour-panel';
import { CdtNavbar } from '@app/nav/cdt-navbar/cdt-navbar';
import { AppUpdateService } from '@app/shared/app-update/app-update.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CdtNavbar, AnomalyBanners, AnnouncementBanners, TourPanel, DemoBanner],

  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class App implements OnInit {
  protected readonly title = signal('vanex-cdt');
  /**
   * Un onglet de démonstration ne montre ni les anomalies ni les nouveautés, qui viennent de l'API ;
   * l'onglet de travail continue de les montrer (RG-parcours-donnees-fictives).
   */
  protected readonly demo = !!inject(DEMO_TOUR_ID, { optional: true });

  readonly documentService = inject(DocumentService);
  readonly validationRequestService = inject(ValidationRequestService);
  private readonly appUpdateService = inject(AppUpdateService);

  private injector = inject(Injector);
  private overlay = inject(Overlay);
  private overlayRef?: OverlayRef;

  ngOnInit(): void {
    this.appUpdateService.start();
    this.documentService.previewDocument$.subscribe((document) => {
      if (document) {
        this.openDocumentPreview(document);
      } else {
        this.closePreview();
      }
    });
    this.validationRequestService.previewValidationRequest$.subscribe((contract) => {
      if (contract) {
        this.openValidationRequestPreview(contract);
      } else {
        this.closePreview();
      }
    });
  }

  openDocumentPreview(documentData: CdTDocument) {
    // cleanup si nécessaire
    this.overlayRef?.dispose();

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy: this.overlay.position().global().right('0').centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      panelClass: 'document-preview-panel',
      width: '90vw',
      height: '100vh',
    });

    const portal = new ComponentPortal(DocumentViewer, null, this.injector);
    const compRef = this.overlayRef.attach(portal);
    compRef.setInput('document', documentData);
    compRef.instance.closed.subscribe(() => this.documentService.closePreview());

    // fermer sur backdrop click et sur ESC
    this.overlayRef.backdropClick().subscribe(() => this.documentService.closePreview());
    this.overlayRef.keydownEvents().subscribe((ev) => {
      if ((ev as KeyboardEvent).key === 'Escape') this.documentService.closePreview();
    });
  }

  openValidationRequestPreview(contract: ContractListItem) {
    // cleanup si nécessaire
    this.overlayRef?.dispose();

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy: this.overlay.position().global().left('0').centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      // FIXME: la classe devrait être contract-history-events
      panelClass: 'contract-history',
      width: '600px',
      height: '100vh',
    });

    const portal = new ComponentPortal(ContractEventHistory, null, this.injector);
    const compRef = this.overlayRef.attach(portal);
    compRef.setInput('contract', contract);

    // fermer sur backdrop click et sur ESC
    this.overlayRef.backdropClick().subscribe(() => this.closePreview());
    this.overlayRef.keydownEvents().subscribe((ev) => {
      if ((ev as KeyboardEvent).key === 'Escape') this.closePreview();
    });
  }

  closePreview() {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }
}
